import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OrderHistoryLoading from "./orders/OrderHistoryLoading";
import OrderList from "./orders/OrderList";
import NoOrders from "./orders/NoOrders";
import OrderFilters from "./orders/OrderFilters";
import { useCart } from "@/contexts/CartContext";
import { getOrderById } from "@/services/orderService";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import PullToRefresh from "@/components/mobile/PullToRefresh";
import { useOrderHistory } from "@/hooks/useOrderHistory";

// Exporting Order for use in other components
export interface Order {
  id: string;
  order_number: string;
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
  tracking_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  order_items_count: number | null;
  order_items?: {
    products: {
      image_url: string | null;
    } | null;
  }[];
}

const OrderHistory = () => {
  const navigate = useNavigate();
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const { addToCart } = useCart();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const {
    orders,
    isLoading,
    refetch,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    hasFilters,
    clearFilters,
  } = useOrderHistory();

  const handleReorder = async (orderId: string) => {
    setReorderingId(orderId);
    toast({
      title: "Reordering...",
      description: "Adding items from your previous order to the cart.",
    });
    try {
      const result = await getOrderById(orderId);
      if (result.success && result.data) {
        const orderItems = result.data.order_items;
        if (!orderItems || orderItems.length === 0) {
          toast({
            title: "Reorder Failed",
            description: "This order has no items to reorder.",
            variant: "destructive",
          });
          return;
        }

        let itemsAdded = 0;
        for (const item of orderItems) {
          if (item.products) {
            addToCart(item.products, item.quantity);
            itemsAdded++;
          }
        }
        
        toast({
          title: "Reorder Successful",
          description: `${itemsAdded} item(s) have been added to your cart.`,
        });
        
      } else {
        throw new Error(result.error || "Failed to fetch order details.");
      }
    } catch (error) {
      // Reorder error handled by notification system
      toast({
        title: "Reorder Failed",
        description: error instanceof Error ? error.message : "An unknown error occurred.",
        variant: "destructive",
      });
    } finally {
      setReorderingId(null);
    }
  };

  const handleViewDetails = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleRefresh = () => {
    refetch();
  };
  
  const handleAsyncRefresh = async () => {
    await refetch();
  };

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order History
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="pb-6">
            <OrderFilters
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              dateFilter={dateFilter}
              onSearchChange={setSearchTerm}
              onStatusChange={setStatusFilter}
              onDateChange={setDateFilter}
            />
          </div>
          <OrderHistoryLoading />
        </CardContent>
      </Card>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <NoOrders
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        onRefresh={handleRefresh}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        dateFilter={dateFilter}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onDateChange={setDateFilter}
      />
    );
  }

  const mainCardContent = (
    <Card variant="glass" className="border-white/10 backdrop-blur-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Order History ({orders.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={handleRefresh} className="bg-transparent hover:bg-white/10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <OrderFilters
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          dateFilter={dateFilter}
          onSearchChange={setSearchTerm}
          onStatusChange={setStatusFilter}
          onDateChange={setDateFilter}
        />
        <OrderList
          orders={orders}
          isMobile={!!isMobile}
          onViewDetails={handleViewDetails}
          onReorder={handleReorder}
          reorderingId={reorderingId}
        />
      </CardContent>
    </Card>
  );

  return (
    <>
      {isMobile ? (
        <PullToRefresh onRefresh={handleAsyncRefresh}>{mainCardContent}</PullToRefresh>
      ) : (
        mainCardContent
      )}
    </>
  );
};

export default OrderHistory;
